import { useEffect } from 'react'
import { debounce } from '@utils/helpers'
import { isEmbedded, postMessageToParent } from '@utils/iframe'
import { useEmbedConfig } from './useEmbedConfig'

export function useIframeHeight() {
  const { heightMode } = useEmbedConfig()

  useEffect(() => {
    // `fill` hosts size the frame themselves.
    if (!isEmbedded() || heightMode === 'fill') {
      return
    }

    const root = document.getElementById('root')
    if (!root) {
      return
    }

    let lastHeight = 0
    const CENTERED_MODAL_MARGIN = 40

    // OmpDrawer/BottomSheet render `position: fixed` content via a portal to
    // document.body, outside #root — its size never shows up in #root's box.
    // Measure it separately and take the max.
    const overlayHeight = () => {
      const drawer = document.querySelector<HTMLElement>('.omp-drawer-content')
      const drawerHeight = drawer
        ? (drawer.querySelector<HTMLElement>('.omp-drawer-head')?.getBoundingClientRect().height ?? 0) +
          (drawer.querySelector<HTMLElement>('.omp-drawer-body')?.scrollHeight ?? 0) +
          (drawer.classList.contains('omp-drawer-center') ? CENTERED_MODAL_MARGIN * 2 : 0)
        : 0

      const sheet = document.querySelector<HTMLElement>('[data-bottom-sheet-content]')
      const sheetHeight = sheet?.getBoundingClientRect().height ?? 0

      return Math.max(drawerHeight, sheetHeight)
    }

    // Measure and observe #root, not documentElement/body: those are stretched to the
    // frame the parent gave us, so they can only ever report growth.
    const sendHeight = debounce(() => {
      const height = Math.ceil(Math.max(root.getBoundingClientRect().height, overlayHeight()))

      // Sub-pixel jitter only; anything larger, up OR down, is a real change.
      if (Math.abs(height - lastHeight) <= 1) {
        return
      }

      lastHeight = height
      postMessageToParent({ type: 'iframeHeight', height })
    }, 100)

    const observer = new ResizeObserver(() => sendHeight())
    observer.observe(root)

    const drawerObserver = new MutationObserver(() => sendHeight())
    drawerObserver.observe(document.body, { childList: true, subtree: true })

    // Resend content height when images load to ensure proper sizing of modals in iframe.
    const handleResourceLoad = (e: Event) => {
      if ((e.target as HTMLElement)?.tagName === 'IMG') {
        sendHeight()
      }
    }
    document.addEventListener('load', handleResourceLoad, true)

    sendHeight()

    return () => {
      observer.disconnect()
      drawerObserver.disconnect()
      document.removeEventListener('load', handleResourceLoad, true)
      sendHeight.cancel()
    }
  }, [heightMode])
}
